import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as VATIcon,
  ArrowBack as BackIcon,
  ShoppingCart as SalesIcon,
  LocalShipping as PurchaseIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";
import ReportDataGrid from "../components/ReportDataGrid";
import reportService from "../services/reportService";
import { useNavigate } from "react-router-dom";
import riyalIcon from "../assets/riyal.png";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/apiService";

const VATReturnReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === "vatadmin";
  const { t, language } = useTranslation();
  const [totals, setTotals] = useState(null);
  const [filters, setFilters] = useState({
    groupBy: "week",
    tenantId: "all",
    startDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - d.getDay());
      return d;
    })(),
    endDate: new Date(),
  });

  const getDateRangeForGroupBy = (groupBy) => {
    const today = new Date();
    let startDate, endDate;

    switch (groupBy) {
      case "day":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "week":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        endDate = new Date(today);
        break;
      case "month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case "year":
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        break;
      default:
        startDate = new Date(today);
        endDate = new Date(today);
    }
    return { startDate, endDate };
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get("/vat-admin/my-companies", {
        params: { limit: "all" },
      });
      const records = res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const formatDate = (date) => {
        if (!date) return undefined;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const params = {
        startDate: formatDate(filters.startDate),
        endDate: formatDate(filters.endDate),
      };
      if (language === "ar" || language === "en") {
        params.language = language;
      }

      // For vatadmin: pass tenantIds when a specific company is selected
      if (isVatAdmin && filters.tenantId && filters.tenantId !== "all") {
        params.tenantIds = filters.tenantId;
      }

      const response = await reportService.getVATReturn(params);

      if (response.success) {
        setReportData(response.data || []);
        setTotals(response.totals || null);
      }
    } catch (error) {
      console.error("Error fetching VAT return report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVatAdmin) {
      fetchCompanies();
    }
    fetchReport();
  }, []);

  const handleFilterChange = (field, value) => {
    if (field === "groupBy") {
      const { startDate, endDate } = getDateRangeForGroupBy(value);
      setFilters((prev) => ({
        ...prev,
        [field]: value,
        startDate,
        endDate,
      }));
    } else {
      setFilters((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const columns = [
    {
      field: "period",
      headerName: t("Reports.Period"),
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: "salesInvoiceCount",
      headerName: t("Reports.Sales_Invoices"),
      flex: 0.8,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
    },
    {
      field: "totalSalesAmount",
      headerName: t("Reports.Sales_Amount"),
      flex: 1,
      minWidth: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "salesAdjustmentAmount",
      headerName: t("Reports.Sales_Adjustment_Amount"),
      flex: 1,
      minWidth: 160,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {(Number(params.value) || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
    },
    {
      field: "totalOutputVAT",
      headerName: t("Reports.Output_VAT"),
      flex: 1,
      minWidth: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "purchaseInvoiceCount",
      headerName: t("Reports.Purchase_Invoices"),
      flex: 0.8,
      minWidth: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
    },

    {
      field: "totalPurchaseAmount",
      headerName: t("Reports.Purchase_Amount"),
      flex: 1,
      minWidth: 140,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "purchaseAdjustmentAmount",
      headerName: t("Reports.Purchase_Adjustment_Amount"),
      flex: 1,
      minWidth: 170,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {(Number(params.value) || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
    },
    {
      field: "totalInputVAT",
      headerName: t("Reports.Input_VAT"),
      flex: 1,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "netVAT",
      headerName: t("Reports.Net_VAT"),
      flex: 1.2,
      minWidth: 140,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          className="font-weight-bold"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
            color: params.value < 0 ? "success.main" : "error.main",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: t("Reports.Status"),
      flex: 1,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isRefundable = params.value === "refundable";
        return (
          <Chip
            label={
              isRefundable
                ? t("Reports.Refundable")
                : params.value === "payable"
                  ? t("Reports.Payable")
                  : "-"
            }
            size="small"
            color={isRefundable ? "success" : "error"}
            variant="outlined"
            sx={{ fontWeight: "bold" }}
          />
        );
      },
    },
  ];

  const summaryCards = [
    {
      title: t("Reports.Total_Input_VAT_Purchases"),
      value: totals?.totalInputVAT || 0,
      icon: <VATIcon />,
      color: "#4361ee",
      format: (val) => (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {val.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
      xs: 12,
      sm: 6,
      md: 2.4,
    },
    {
      title: t("Common.Debit_Notes"),
      value: totals?.purchaseAdjustmentAmount || 0,
      icon: <VATIcon />,
      color: "#4361ee",
      format: (val) => (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {val.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
      xs: 12,
      sm: 6,
      md: 2.4,
    },

    {
      title: t("Reports.Total_Output_VAT_Sales"),
      value: totals?.totalOutputVAT || 0,
      icon: <VATIcon />,
      color: "#ed6c02",
      format: (val) => (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {val.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
      xs: 12,
      sm: 6,
      md: 2.4,
    },
    {
      title: t("sidebar.Credit_Note"),
      value: totals?.salesAdjustmentAmount || 0,
      icon: <VATIcon />,
      color: "#ed6c02",
      format: (val) => (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px" }}
          />
          <span>
            {val.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
      xs: 12,
      sm: 6,
      md: 2.4,
    },
    {
      title: t("Reports.Net_VAT_Payable"),
      value: totals?.netVAT || 0,
      icon: <CalculateIcon />,
      color: (totals?.netVAT || 0) < 0 ? "#2e7d32" : "#d32f2f",
      format: (val) => (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "nowrap",
            whiteSpace: "nowrap",
            color: val < 0 ? "success.main" : "error.main",
          }}
        >
          <img
            src={riyalIcon}
            alt="currency"
            style={{ width: "15px", height: "15px", flexShrink: 0 }}
          />
          <span>
            {val.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <span>
            {/* {val < 0
              ? `(${t("Reports.Refundable")})`
              : `(${t("Reports.Payable")})`} */}
          </span>
        </Box>
      ),
      xs: 12,
      sm: 12,
      md: 2.4,
    },
  ];

  const handleBack = () => {
    navigate("/reports");
  };

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      <Box sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={handleBack}
          sx={{ mr: 1.5, bgcolor: "rgba(67, 97, 238, 0.1)" }}
        >
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            {t("Reports.VAT_Return_Summary")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Reports.VAT_Return_Description")}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Typography
          variant="caption"
          fontWeight="600"
          sx={{ mb: 1, display: "block", fontSize: "0.75rem" }}
        >
          {t("Common.Filters")}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} alignItems="center" sx={{ mt: 0 }}>
            {/* Group By */}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <TextField
                fullWidth
                select
                label={t("Reports.Group_By")}
                value={filters.groupBy}
                onChange={(e) => handleFilterChange("groupBy", e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    height: "36px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                  "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                }}
              >
                <MenuItem value="day">{t("Reports.Daily")}</MenuItem>
                <MenuItem value="week">{t("Reports.Weekly")}</MenuItem>
                <MenuItem value="month">{t("Reports.Monthly")}</MenuItem>
                <MenuItem value="year">{t("Reports.Yearly")}</MenuItem>
              </TextField>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <DatePicker
                label={t("Reports.Start_Date")}
                value={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        height: "36px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                      "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                    },
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <DatePicker
                label={t("Reports.End_Date")}
                value={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        height: "36px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                      "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                    },
                  },
                }}
              />
            </Grid>

            {/* Company dropdown — vatadmin only */}
            {isVatAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t("Common.Company_Name") || "Company"}
                  value={filters.tenantId}
                  onChange={(e) =>
                    handleFilterChange("tenantId", e.target.value)
                  }
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      height: "36px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                  }}
                >
                  <MenuItem value="all">
                    {t("Common.All_Companies") || "All Companies"}
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.tenantId} value={company.tenantId}>
                      {company.name || company.tenantId}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Apply Filters */}
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  borderRadius: 0,
                  height: "36px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  t("Reports.Apply_Filters")
                )}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {summaryCards.map((card, index) => (
            <Grid
              item
              xs={card.xs}
              sm={card.sm}
              md={card.md}
              key={index}
              sx={{
                /* 901-1414px: 3 cards in row 1, 2 cards in row 2 */
                "@media (min-width: 901px) and (max-width: 1414px)": {
                  flexBasis: index < 3 ? "33.333%" : "50%",
                  maxWidth: index < 3 ? "33.333%" : "50%",
                  flexGrow: 0,
                  flexShrink: 0,
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  borderLeft: `4px solid ${card.color}`,
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: "8px",
                      bgcolor: `${card.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1.5,
                    }}
                  >
                    {React.cloneElement(card.icon, {
                      sx: { color: card.color, fontSize: 20 },
                    })}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      sx={{
                        overflow: "hidden",
                        lineHeight: 1.3,
                        "@media (min-width: 901px) and (max-width: 1414px)": {
                          fontSize: index === 4 ? "0.82rem" : "1rem",
                        },
                      }}
                    >
                      {card.format(card.value)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ReportDataGrid
        title={t("Reports.VAT_Return_Report")}
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`vat-return-summary-${new Date().toISOString().split("T")[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) => row.period || Math.random().toString()}
        totals={totals}
        dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
      />
    </Container>
  );
};

export default VATReturnReport;
